import { useEffect, useMemo, useState } from "react";
import { 
  Ticket, 
  Settings, 
  Users, 
  CheckCircle, 
  Clock,
  MessageSquare,
  Save,
  Loader2,
  Plus,
  Trash2,
  Check,
  ChevronsUpDown,
  ListChecks,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { pluginApi, serverApi, ticketsApi } from "@/lib/api";

interface TicketSettings {
  serverId: string;
  categoryId: string | null;
  staffRoleId: string | null;
  logChannelId: string | null;
  maxTicketsPerUser: number;
  welcomeMessage: string;
  enabled: boolean;
}

interface TicketStats {
  totalTickets: number;
  uniqueUsers: number;
  openTickets: number;
  closedTickets: number;
}

interface Ticket {
  id: string;
  userId: string;
  channelId: string;
  subject: string | null;
  status: string;
  assignedTo: string | null;
  createdAt: string;
  closedAt: string | null;
}

interface TicketsPluginConfig {
  ticketPanelChannelId: string;
  ticketPanelChannelIdsCsv: string;
  ticketClosedCategoryId: string;
  supportTicketRoleId: string;
  supportTicketRoleIdsCsv: string;
  requireStaffConfirmation: boolean;
  aiAutoReplyEnabled: boolean;
  aiMaxAutoReplies: number;
  ticketQueuesJson: string;
  ticketReasonsCsv: string;
  panelTitle?: string;
  panelDescription?: string;
  panelButtonLabel?: string;
  queuePlaceholder?: string;
  queueSelectionTitle?: string;
  queueSelectionDescription?: string;
}

interface TicketQueueEditorItem {
  id: string;
  label: string;
  panelChannelId: string;
  categoryId: string;
  closedCategoryId: string;
  subcategoriesCsv: string;
  staffRoleIds: string[];
  userRoleIds: string[];
  emoji?: string;
  rulesText?: string;
}

interface SearchableOption {
  id: string;
  label: string;
}

function SearchableAddSelect({
  options,
  selectedIds,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  onAdd,
}: {
  options: SearchableOption[];
  selectedIds: string[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  onAdd: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const availableOptions = options.filter((option) => !selectedIds.includes(option.id));
  const triggerLabel = selectedIds.length > 0 ? `${placeholder} (${selectedIds.length})` : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {triggerLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {availableOptions.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.label}
                  onSelect={() => {
                    onAdd(option.id);
                    setOpen(false);
                  }}
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function parseBooleanConfig(value: unknown, defaultValue = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', ''].includes(normalized)) return false;
  }
  return defaultValue;
}

export const TicketsPlugin = ({ serverId }: { serverId: string }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createSubject, setCreateSubject] = useState('');
  const [createCategory, setCreateCategory] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [assignInputs, setAssignInputs] = useState<Record<string, string>>({});
  const [ticketsPluginConfig, setTicketsPluginConfig] = useState<TicketsPluginConfig>({
    ticketPanelChannelId: '',
    ticketPanelChannelIdsCsv: '',
    ticketClosedCategoryId: '',
    supportTicketRoleId: '',
    supportTicketRoleIdsCsv: '',
    requireStaffConfirmation: false,
    aiAutoReplyEnabled: false,
    aiMaxAutoReplies: 3,
    ticketQueuesJson: '[]',
    ticketReasonsCsv: '',
    panelTitle: '',
    panelDescription: '',
    panelButtonLabel: '',
    queuePlaceholder: '',
    queueSelectionTitle: '',
    queueSelectionDescription: '',
  });

  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['servers', serverId, 'categories'],
    queryFn: () => serverApi.getCategories(serverId),
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const ticketsPlugin = (plugins as any[])?.find((p: any) => p.pluginId === 'tickets');
  const isEnabled = ticketsPlugin?.enabled ?? false;

  // Fetch settings
  const { data: settings, isLoading: settingsLoading } = useQuery<TicketSettings>({
    queryKey: ['servers', serverId, 'tickets', 'settings'],
    queryFn: () => ticketsApi.getSettings(serverId),
  });

  // Fetch stats
  const { data: stats } = useQuery<TicketStats>({
    queryKey: ['servers', serverId, 'tickets', 'stats'],
    queryFn: () => ticketsApi.getStats(serverId),
  });

  // Fetch tickets
  const { data: tickets = [] } = useQuery<Ticket[]>({
    queryKey: ['servers', serverId, 'tickets'],
    queryFn: () => ticketsApi.getTickets(serverId),
  });

  const [config, setConfig] = useState<TicketSettings>({
    serverId,
    categoryId: null,
    staffRoleId: null,
    logChannelId: null,
    maxTicketsPerUser: 1,
    welcomeMessage: 'Welcome! A staff member will assist you shortly.',
    enabled: false,
  });

  const availableCategories = ((categories as any[]).length > 0
    ? (categories as any[])
    : (channels as any[]).filter((channel: any) => {
        const type = String(channel?.type || '').toLowerCase();
        return type.includes('category') || type === '4';
      })) as any[];

  const quickSetupChecklist = {
    category: !!config.categoryId,
    staffRole: !!config.staffRoleId,
    panelChannel: !!ticketsPluginConfig.ticketPanelChannelId,
    closedCategory: !!ticketsPluginConfig.ticketClosedCategoryId,
  };

  const configuredSteps = Object.values(quickSetupChecklist).filter(Boolean).length;

  const parseCsvIds = (value: string) =>
    String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const toggleCsvId = (csv: string, id: string) => {
    const current = new Set(parseCsvIds(csv));
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    return Array.from(current).join(', ');
  };

  const addCsvId = (csv: string, id: string) => {
    const current = new Set(parseCsvIds(csv));
    current.add(id);
    return Array.from(current).join(', ');
  };

  const removeCsvId = (csv: string, id: string) => {
    const current = new Set(parseCsvIds(csv));
    current.delete(id);
    return Array.from(current).join(', ');
  };

  const handleGenerateQueueTemplate = () => {
    const selectedCategoryId = config.categoryId || '';
    const selectedStaffRoleIds = parseCsvIds(ticketsPluginConfig.supportTicketRoleIdsCsv || ticketsPluginConfig.supportTicketRoleId);

    const roleIdByName = new Map<string, string>();
    (roles as any[]).forEach((role: any) => {
      const key = String(role?.name || '').trim().toLowerCase();
      if (key) {
        roleIdByName.set(key, String(role.id));
      }
    });

    const findRoleIdByKeyword = (keyword: string) => {
      const normalized = keyword.toLowerCase();
      const direct = roleIdByName.get(normalized);
      if (direct) return direct;
      for (const [name, id] of roleIdByName.entries()) {
        if (name.includes(normalized)) return id;
      }
      return '';
    };

    const gameDefs = [
      { id: 'fivem', label: 'FiveM', emoji: '🚗', roleKeyword: 'fivem' },
      { id: 'redm', label: 'RedM', emoji: '🤠', roleKeyword: 'redm' },
      { id: 'scum', label: 'SCUM', emoji: '🧟', roleKeyword: 'scum' },
      { id: 'arma', label: 'Arma', emoji: '🪖', roleKeyword: 'arma' },
      { id: 'starcitizen', label: 'Star Citizen', emoji: '🚀', roleKeyword: 'star citizen' },
    ];

    const template = gameDefs.map((game) => {
      const userRoleId = findRoleIdByKeyword(game.roleKeyword);
      return {
        id: game.id,
        label: game.label,
        panelChannelId: ticketsPluginConfig.ticketPanelChannelId || '',
        categoryId: selectedCategoryId || '',
        closedCategoryId: ticketsPluginConfig.ticketClosedCategoryId || '',
        staffRoleIds: selectedStaffRoleIds,
        userRoleIds: userRoleId ? [userRoleId] : [],
        emoji: game.emoji,
        rulesText: '',
      };
    });

    setTicketsPluginConfig((prev) => ({
      ...prev,
      ticketQueuesJson: JSON.stringify(template, null, 2),
    }));
  };

  const parsedTicketQueues = useMemo<TicketQueueEditorItem[]>(() => {
    try {
      const parsed = JSON.parse(ticketsPluginConfig.ticketQueuesJson || '[]');
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map((queue: any, index: number) => ({
        id: String(queue?.id || `queue-${index + 1}`),
        label: String(queue?.label || ''),
        panelChannelId: String(queue?.panelChannelId || ''),
        categoryId: String(queue?.categoryId || ''),
        closedCategoryId: String(queue?.closedCategoryId || ''),
        subcategoriesCsv: Array.isArray(queue?.subcategories)
          ? queue.subcategories.join(', ')
          : String(queue?.subcategoriesCsv || ''),
        staffRoleIds: Array.isArray(queue?.staffRoleIds)
          ? queue.staffRoleIds.map((roleId: any) => String(roleId)).filter(Boolean)
          : [],
        userRoleIds: Array.isArray(queue?.userRoleIds)
          ? queue.userRoleIds.map((roleId: any) => String(roleId)).filter(Boolean)
          : [],
        emoji: queue?.emoji ? String(queue.emoji) : '',
        rulesText: String(queue?.rulesText || queue?.rules || ''),
      }));
    } catch {
      return [];
    }
  }, [ticketsPluginConfig.ticketQueuesJson]);

  const updateQueuesJsonFromEditor = (queues: TicketQueueEditorItem[]) => {
    setTicketsPluginConfig((prev) => ({
      ...prev,
      ticketQueuesJson: JSON.stringify(queues, null, 2),
    }));
  };

  const updateQueueField = (queueId: string, field: keyof TicketQueueEditorItem, value: string) => {
    const updatedQueues = parsedTicketQueues.map((queue) =>
      queue.id === queueId ? { ...queue, [field]: value } : queue
    );
    updateQueuesJsonFromEditor(updatedQueues);
  };

  const updateQueueSubcategories = (queueId: string, subcategories: string[]) => {
    updateQueueField(queueId, 'subcategoriesCsv', subcategories.join(', '));
  };

  const toggleQueueRole = (queueId: string, field: 'staffRoleIds' | 'userRoleIds', roleId: string) => {
    const updatedQueues = parsedTicketQueues.map((queue) => {
      if (queue.id !== queueId) return queue;

      const roleSet = new Set(queue[field]);
      if (roleSet.has(roleId)) {
        roleSet.delete(roleId);
      } else {
        roleSet.add(roleId);
      }

      return {
        ...queue,
        [field]: Array.from(roleSet),
      };
    });

    updateQueuesJsonFromEditor(updatedQueues);
  };

  const addQueue = () => {
    const nextIndex = parsedTicketQueues.length + 1;
    const defaultStaffRoleIds = parseCsvIds(
      ticketsPluginConfig.supportTicketRoleIdsCsv || ticketsPluginConfig.supportTicketRoleId
    );

    const newQueue: TicketQueueEditorItem = {
      id: `queue-${nextIndex}`,
      label: `Queue ${nextIndex}`,
      panelChannelId: ticketsPluginConfig.ticketPanelChannelId || '',
      categoryId: config.categoryId || '',
      closedCategoryId: ticketsPluginConfig.ticketClosedCategoryId || '',
      subcategoriesCsv: '',
      staffRoleIds: defaultStaffRoleIds,
      userRoleIds: [],
      emoji: '',
      rulesText: '',
    };

    updateQueuesJsonFromEditor([...parsedTicketQueues, newQueue]);
  };

  const removeQueue = (queueId: string) => {
    updateQueuesJsonFromEditor(parsedTicketQueues.filter((queue) => queue.id !== queueId));
  };

  // Update config when settings load
  useEffect(() => {
    if (settings) {
      setConfig(settings);
    }
  }, [settings]);

  useEffect(() => {
    const plugin = (plugins as any[])?.find((p: any) => p.pluginId === 'tickets');
    if (!plugin?.config) return;

    setTicketsPluginConfig({
      ticketPanelChannelId: String(plugin.config.ticketPanelChannelId || ''),
      ticketPanelChannelIdsCsv: Array.isArray(plugin.config.ticketPanelChannelIds)
        ? plugin.config.ticketPanelChannelIds.join(', ')
        : String(plugin.config.ticketPanelChannelIds || ''),
      ticketClosedCategoryId: String(plugin.config.ticketClosedCategoryId || ''),
      supportTicketRoleId: String(plugin.config.supportTicketRoleId || ''),
      supportTicketRoleIdsCsv: Array.isArray(plugin.config.supportTicketRoleIds)
        ? plugin.config.supportTicketRoleIds.join(', ')
        : String(plugin.config.supportTicketRoleIds || ''),
      requireStaffConfirmation: !!plugin.config.requireStaffConfirmation,
      aiAutoReplyEnabled: parseBooleanConfig(plugin.config.aiAutoReplyEnabled),
      aiMaxAutoReplies: Math.max(1, Number(plugin.config.aiMaxAutoReplies || 3)),
      ticketQueuesJson: JSON.stringify(plugin.config.ticketQueues || [], null, 2),
      ticketReasonsCsv: Array.isArray(plugin.config.ticketReasons)
        ? plugin.config.ticketReasons.join(', ')
        : String(plugin.config.ticketReasons || ''),
      panelTitle: String(plugin.config.panelTitle || ''),
      panelDescription: String(plugin.config.panelDescription || ''),
      panelButtonLabel: String(plugin.config.panelButtonLabel || ''),
      queuePlaceholder: String(plugin.config.queuePlaceholder || ''),
      queueSelectionTitle: String(plugin.config.queueSelectionTitle || ''),
      queueSelectionDescription: String(plugin.config.queueSelectionDescription || ''),
    });
  }, [plugins]);

  const createTicketMutation = useMutation({
    mutationFn: async () =>
      ticketsApi.createTicket(serverId, {
        userId: createUserId,
        subject: createSubject,
        category: createCategory,
        description: createDescription,
      }),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Ticket creato con successo!',
      });
      setCreateSubject('');
      setCreateCategory('');
      setCreateDescription('');
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets', 'stats'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Errore durante la creazione del ticket',
        variant: 'destructive',
      });
    },
  });

  const assignTicketMutation = useMutation({
    mutationFn: async ({ ticketId, userId }: { ticketId: string; userId?: string }) => {
      return ticketsApi.assignTicket(serverId, ticketId, userId);
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Ticket assegnato con successo!',
      });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Errore durante assegnazione ticket',
        variant: 'destructive',
      });
    },
  });

  const closeTicketMutation = useMutation({
    mutationFn: async (ticketId: string) => ticketsApi.closeTicket(serverId, ticketId),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Ticket chiuso con successo!',
      });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets', 'stats'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Errore durante chiusura ticket',
        variant: 'destructive',
      });
    },
  });

  const spawnPanelMutation = useMutation({
    mutationFn: async () => {
      const selectedPanelChannelIds = parseCsvIds(ticketsPluginConfig.ticketPanelChannelIdsCsv);
      const uniqueTargetChannels = Array.from(
        new Set(
          selectedPanelChannelIds.length > 0
            ? selectedPanelChannelIds
            : [ticketsPluginConfig.ticketPanelChannelId].filter(Boolean)
        )
      );

      if (uniqueTargetChannels.length === 0) {
        return {
          successes: [],
          failures: [],
        };
      }

      const results = await Promise.allSettled(
        uniqueTargetChannels.map((channelId) => ticketsApi.spawnPanel(serverId, channelId))
      );

      const successes = results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map((result) => result.value);

      const failures = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => result.reason);

      return { successes, failures };
    },
    onSuccess: (result: any) => {
      const successCount = Array.isArray(result?.successes) ? result.successes.length : 0;
      const failureCount = Array.isArray(result?.failures) ? result.failures.length : 0;

      if (successCount === 0 && failureCount === 0) {
        toast({
          title: 'Attenzione',
          description: 'Seleziona almeno un canale pannello prima della pubblicazione.',
          variant: 'destructive',
        });
        return;
      }

      if (successCount > 0 && failureCount === 0) {
        toast({
          title: 'Success',
          description: successCount === 1
            ? 'Embed ticket pubblicato con successo.'
            : `Embed ticket pubblicato in ${successCount} canali.`,
        });
        return;
      }

      if (successCount > 0 && failureCount > 0) {
        toast({
          title: 'Parzialmente completato',
          description: `Pubblicati ${successCount} pannelli, ${failureCount} non riusciti.`,
        });
        return;
      }

      toast({
        title: 'Error',
        description: 'Nessun pannello pubblicato. Controlla permessi e canali selezionati.',
        variant: 'destructive',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Errore pubblicazione embed ticket',
        variant: 'destructive',
      });
    },
  });

  const savePluginConfigMutation = useMutation({
    mutationFn: async () => {
      const supportTicketRoleIds = ticketsPluginConfig.supportTicketRoleIdsCsv
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const ticketPanelChannelIds = ticketsPluginConfig.ticketPanelChannelIdsCsv
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const parsedQueues = parsedTicketQueues.map((queue) => ({
        ...queue,
        id: queue.id.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-') || `queue-${Date.now()}`,
        label: queue.label.trim() || 'Supporto',
        subcategories: parseCsvIds(queue.subcategoriesCsv),
        staffRoleIds: queue.staffRoleIds.filter(Boolean),
        userRoleIds: queue.userRoleIds.filter(Boolean),
      }));

      const ticketReasons = ticketsPluginConfig.ticketReasonsCsv
        .split(',')
        .map((reason) => reason.trim())
        .filter(Boolean);

      return pluginApi.updatePluginConfig(serverId, 'tickets', {
        ticketPanelChannelId: ticketsPluginConfig.ticketPanelChannelId,
        ticketPanelChannelIds,
        ticketClosedCategoryId: ticketsPluginConfig.ticketClosedCategoryId,
        supportTicketRoleId: ticketsPluginConfig.supportTicketRoleId,
        supportTicketRoleIds,
        requireStaffConfirmation: ticketsPluginConfig.requireStaffConfirmation,
        aiAutoReplyEnabled: ticketsPluginConfig.aiAutoReplyEnabled,
        aiMaxAutoReplies: ticketsPluginConfig.aiMaxAutoReplies,
        ticketQueues: parsedQueues,
        ticketReasons,
        panelTitle: ticketsPluginConfig.panelTitle,
        panelDescription: ticketsPluginConfig.panelDescription,
        panelButtonLabel: ticketsPluginConfig.panelButtonLabel,
        queuePlaceholder: ticketsPluginConfig.queuePlaceholder,
        queueSelectionTitle: ticketsPluginConfig.queueSelectionTitle,
        queueSelectionDescription: ticketsPluginConfig.queueSelectionDescription,
      });
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Configurazione avanzata Tickets salvata!' });
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Errore salvataggio configurazione Tickets',
        variant: 'destructive',
      });
    },
  });

  const togglePluginMutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      enabled ? pluginApi.enablePlugin(serverId, 'tickets') : pluginApi.disablePlugin(serverId, 'tickets'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'plugins'] });
      toast({ title: 'Success', description: 'Plugin Tickets aggiornato' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Errore aggiornamento stato plugin Tickets',
        variant: 'destructive',
      });
    },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await ticketsApi.updateSettings(serverId, config);

      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'tickets'] });
      toast({
        title: "Success",
        description: "Ticket settings saved successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTicket = () => {
    if (!createUserId.trim()) {
      toast({
        title: 'Error',
        description: 'Inserisci userId per creare ticket',
        variant: 'destructive',
      });
      return;
    }
    createTicketMutation.mutate();
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col gap-4 rounded-xl border border-primary/20 bg-primary/5 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <Ticket className="w-8 h-8 text-primary" />
            Configurazione Ticket
          </h2>
          <p className="text-muted-foreground mt-1">
            Imposta il flusso in Discord: pannello, categorie, staff e risposte AI.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-lg border bg-background/60 px-4 py-3">
          <div>
            <p className="text-sm font-semibold">Plugin Tickets</p>
            <p className="text-xs text-muted-foreground">{isEnabled ? 'Attivo su Discord' : 'Disattivato su Discord'}</p>
          </div>
          <Switch
            checked={isEnabled}
            disabled={togglePluginMutation.isPending}
            onCheckedChange={(enabled) => togglePluginMutation.mutate(enabled)}
          />
        </div>
      </div>

      <Card className="glass-card border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-primary" /> Percorso consigliato</CardTitle>
          <CardDescription>Completa i primi quattro passaggi, salva ogni sezione e pubblica il pannello in Discord.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg bg-background/60 px-4 py-3 text-sm">
            <span>Configurazione essenziale</span>
            <span className="font-semibold text-primary">{configuredSteps}/4 completati</span>
          </div>
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            {[
              ['1', 'Categoria apertura', 'Dove vengono creati i ticket.', quickSetupChecklist.category],
              ['2', 'Ruolo staff', 'Chi può vedere e gestire i ticket.', quickSetupChecklist.staffRole],
              ['3', 'Canale pannello', 'Dove pubblicare il pulsante Apri Ticket.', quickSetupChecklist.panelChannel],
              ['4', 'Categoria chiusi', 'Dove archiviare i ticket chiusi.', quickSetupChecklist.closedCategory],
            ].map(([number, title, description, done]) => (
              <div key={number} className="flex gap-3 rounded-lg border border-border/70 bg-background/40 p-3">
                <span className={done ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 font-bold text-emerald-500' : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 font-bold text-amber-500'}>{done ? '✓' : number}</span>
                <div><p className="font-semibold">{title}</p><p className="text-xs text-muted-foreground">{description}</p></div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 text-blue-500" />
            <span>Per più giochi o reparti, usa le queue nella sezione Instradamento: ogni queue può avere canale, categoria di apertura e categoria di chiusura diversi.</span>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Ticket Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalTickets}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utenti Unici
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.uniqueUsers}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ticket Aperti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-500">{stats.openTickets}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Ticket Chiusi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">{stats.closedTickets}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Setup Rapido */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Impostazioni di base
          </CardTitle>
          <CardDescription>
            Servono per il flusso standard. Se usi le queue, i loro valori specifici hanno priorità.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="categoryId">Categoria Ticket</Label>
              <Select
                value={config.categoryId || 'none'}
                onValueChange={(value) => setConfig({ ...config, categoryId: value === 'none' ? null : value })}
              >
                <SelectTrigger id="categoryId">
                  <SelectValue placeholder="Seleziona categoria ticket" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna categoria</SelectItem>
                  {availableCategories.map((category: any) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                I ticket verranno creati in questa categoria
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="staffRoleId">Ruolo Staff</Label>
              <Select
                value={config.staffRoleId || 'none'}
                onValueChange={(value) => setConfig({ ...config, staffRoleId: value === 'none' ? null : value })}
              >
                <SelectTrigger id="staffRoleId">
                  <SelectValue placeholder="Seleziona ruolo staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun ruolo</SelectItem>
                  {(roles as any[]).map((role: any) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Membri con questo ruolo potranno vedere i ticket
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logChannelId">Canale Log</Label>
              <Select
                value={config.logChannelId || 'none'}
                onValueChange={(value) => setConfig({ ...config, logChannelId: value === 'none' ? null : value })}
              >
                <SelectTrigger id="logChannelId">
                  <SelectValue placeholder="Seleziona canale log" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Eventi ticket verranno loggati qui
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxTickets">Max Ticket per Utente</Label>
              <Input
                id="maxTickets"
                type="number"
                min="1"
                max="10"
                value={config.maxTicketsPerUser}
                onChange={(e) => setConfig({ ...config, maxTicketsPerUser: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">
                Numero massimo di ticket aperti contemporaneamente
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="welcomeMessage">Messaggio di Benvenuto</Label>
            <Textarea
              id="welcomeMessage"
              rows={4}
              placeholder="Messaggio mostrato quando si apre un ticket"
              value={config.welcomeMessage}
              onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Usa @user per menzionare l'utente
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <div>
              <p className="font-medium">Comandi ticket attivi</p>
              <p className="text-sm text-muted-foreground">
                Gli utenti potranno usare /ticket quando il plugin è attivo
              </p>
            </div>
            <Button
              variant={config.enabled ? "default" : "outline"}
              onClick={() => setConfig({ ...config, enabled: !config.enabled })}
            >
              {config.enabled ? "Attivo" : "Disattivato"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create Ticket */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Crea Ticket di test</CardTitle>
          <CardDescription>
            Utile solo per verificare l’API dalla dashboard. Gli utenti normali usano il pannello Discord.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="createUserId">User ID</Label>
              <Input
                id="createUserId"
                value={createUserId}
                onChange={(e) => setCreateUserId(e.target.value)}
                placeholder="ID utente interno"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="createCategory">Categoria (opzionale)</Label>
              <Select value={createCategory || 'none'} onValueChange={(value) => setCreateCategory(value === 'none' ? '' : value)}>
                <SelectTrigger id="createCategory">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna categoria</SelectItem>
                  {availableCategories.map((category: any) => (
                    <SelectItem key={category.id} value={category.name}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="createSubject">Oggetto</Label>
              <Input
                id="createSubject"
                value={createSubject}
                onChange={(e) => setCreateSubject(e.target.value)}
                placeholder="Oggetto ticket"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="createDescription">Descrizione</Label>
              <Textarea
                id="createDescription"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                rows={3}
                placeholder="Descrizione problema o richiesta"
              />
            </div>
          </div>

          <Button onClick={handleCreateTicket} disabled={createTicketMutation.isPending}>
            {createTicketMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
            Crea Ticket
          </Button>
        </CardContent>
      </Card>

      {/* Advanced Plugin Config */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Instradamento e automazioni</CardTitle>
          <CardDescription>
            Qui decidi dove pubblicare il pannello, come dividere i ticket per categoria e se attivare l’AI. Non serve inserire JSON.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ticketPanelChannelId">Canale Pannello Ticket</Label>
              <Select
                value={ticketsPluginConfig.ticketPanelChannelId || 'none'}
                onValueChange={(value) =>
                  setTicketsPluginConfig((prev) => ({ ...prev, ticketPanelChannelId: value === 'none' ? '' : value }))
                }
              >
                <SelectTrigger id="ticketPanelChannelId">
                  <SelectValue placeholder="Seleziona canale pannello" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun canale</SelectItem>
                  {(channels as any[]).map((channel: any) => (
                    <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticketClosedCategoryId">Categoria Ticket Chiusi</Label>
              <Select
                value={ticketsPluginConfig.ticketClosedCategoryId || 'none'}
                onValueChange={(value) =>
                  setTicketsPluginConfig((prev) => ({ ...prev, ticketClosedCategoryId: value === 'none' ? '' : value }))
                }
              >
                <SelectTrigger id="ticketClosedCategoryId">
                  <SelectValue placeholder="Seleziona categoria ticket chiusi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna categoria</SelectItem>
                  {availableCategories.map((category: any) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supportTicketRoleId">Ruolo Support Ticket (fallback)</Label>
              <Select
                value={ticketsPluginConfig.supportTicketRoleId || 'none'}
                onValueChange={(value) =>
                  setTicketsPluginConfig((prev) => ({ ...prev, supportTicketRoleId: value === 'none' ? '' : value }))
                }
              >
                <SelectTrigger id="supportTicketRoleId">
                  <SelectValue placeholder="Seleziona ruolo supporto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun ruolo</SelectItem>
                  {(roles as any[]).map((role: any) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supportTicketRoleIdsCsv">Ruoli Staff Multipli</Label>
              <div id="supportTicketRoleIdsCsv" className="rounded-md border border-input bg-background p-3 space-y-2">
                <SearchableAddSelect
                  options={(roles as any[]).map((role: any) => ({ id: role.id, label: role.name }))}
                  selectedIds={parseCsvIds(ticketsPluginConfig.supportTicketRoleIdsCsv)}
                  placeholder="Aggiungi ruolo staff"
                  searchPlaceholder="Cerca ruolo..."
                  emptyLabel="Nessun ruolo disponibile"
                  onAdd={(id) =>
                    setTicketsPluginConfig((prev) => ({
                      ...prev,
                      supportTicketRoleIdsCsv: addCsvId(prev.supportTicketRoleIdsCsv, id),
                    }))
                  }
                />
                <div className="flex flex-wrap gap-2">
                  {parseCsvIds(ticketsPluginConfig.supportTicketRoleIdsCsv).map((roleId) => {
                    const role = (roles as any[]).find((item: any) => item.id === roleId);
                    return (
                      <Button
                        key={roleId}
                        type="button"
                        size="sm"
                        variant="default"
                        onClick={() =>
                          setTicketsPluginConfig((prev) => ({
                            ...prev,
                            supportTicketRoleIdsCsv: removeCsvId(prev.supportTicketRoleIdsCsv, roleId),
                          }))
                        }
                      >
                        {role?.name || roleId} <Trash2 className="w-3 h-3 ml-2" />
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">Seleziona uno o più ruoli staff senza copiare ID.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticketPanelChannelIdsCsv">Canali Pannello Multipli</Label>
              <div id="ticketPanelChannelIdsCsv" className="rounded-md border border-input bg-background p-3 space-y-2">
                <SearchableAddSelect
                  options={(channels as any[]).map((channel: any) => ({ id: channel.id, label: `#${channel.name}` }))}
                  selectedIds={parseCsvIds(ticketsPluginConfig.ticketPanelChannelIdsCsv)}
                  placeholder="Aggiungi canale pannello"
                  searchPlaceholder="Cerca canale..."
                  emptyLabel="Nessun canale disponibile"
                  onAdd={(id) =>
                    setTicketsPluginConfig((prev) => ({
                      ...prev,
                      ticketPanelChannelIdsCsv: addCsvId(prev.ticketPanelChannelIdsCsv, id),
                    }))
                  }
                />
                <div className="flex flex-wrap gap-2">
                  {parseCsvIds(ticketsPluginConfig.ticketPanelChannelIdsCsv).map((channelId) => {
                    const channel = (channels as any[]).find((item: any) => item.id === channelId);
                    return (
                      <Button
                        key={channelId}
                        type="button"
                        size="sm"
                        variant="default"
                        onClick={() =>
                          setTicketsPluginConfig((prev) => ({
                            ...prev,
                            ticketPanelChannelIdsCsv: removeCsvId(prev.ticketPanelChannelIdsCsv, channelId),
                          }))
                        }
                      >
                        #{channel?.name || channelId} <Trash2 className="w-3 h-3 ml-2" />
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">Seleziona uno o più canali pannello senza copiare ID.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="requireStaffConfirmation">Richiedi conferma staff per chiudere</Label>
              <div className="h-10 px-3 rounded-md border border-input bg-background flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {ticketsPluginConfig.requireStaffConfirmation ? 'Attiva' : 'Disattiva'} conferma staff
                </span>
                <Switch
                  id="requireStaffConfirmation"
                  checked={ticketsPluginConfig.requireStaffConfirmation}
                  onCheckedChange={(checked) => setTicketsPluginConfig((prev) => ({ ...prev, requireStaffConfirmation: checked }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="aiAutoReplyEnabled">IA automatica nel ticket (facoltativa)</Label>
              <div className={ticketsPluginConfig.aiAutoReplyEnabled ? 'rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3' : 'rounded-md border border-input bg-background p-3'}>
                <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {ticketsPluginConfig.aiAutoReplyEnabled ? 'Attiva' : 'Disattiva'} risposta AI automatica
                </span>
                <Switch
                  id="aiAutoReplyEnabled"
                  checked={ticketsPluginConfig.aiAutoReplyEnabled}
                  onCheckedChange={(checked) => setTicketsPluginConfig((prev) => ({ ...prev, aiAutoReplyEnabled: checked }))}
                />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {ticketsPluginConfig.aiAutoReplyEnabled
                    ? 'L’AI risponde ai messaggi dell’utente prima della presa in carico staff.'
                    : 'L’AI non risponde e lo staff può prendere in carico subito il ticket.'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="aiMaxAutoReplies">Max risposte AI per ticket</Label>
              <Input
                id="aiMaxAutoReplies"
                type="number"
                min="1"
                max="20"
                value={ticketsPluginConfig.aiMaxAutoReplies}
                disabled={!ticketsPluginConfig.aiAutoReplyEnabled}
                onChange={(e) =>
                  setTicketsPluginConfig((prev) => ({
                    ...prev,
                    aiMaxAutoReplies: Math.max(1, Number(e.target.value || 1)),
                  }))
                }
              />
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Queue e categorie</Label>
                  <p className="mt-1 text-xs text-muted-foreground">Ogni queue è una destinazione indipendente per un tipo di richiesta.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={handleGenerateQueueTemplate}>
                    Crea queue di esempio
                  </Button>
                  <Button type="button" size="sm" onClick={addQueue}>
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi Queue
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {parsedTicketQueues.length === 0 && (
                  <div className="rounded-md border border-dashed border-input p-4 text-sm text-muted-foreground">
                    Nessuna queue personalizzata. In questo caso il bot usa una queue Supporto standard. Aggiungi giochi solo per instradare ticket in categorie diverse.
                  </div>
                )}

                {parsedTicketQueues.map((queue, queueIndex) => (
                  <div key={queue.id} className="space-y-4 rounded-xl border border-primary/20 bg-background/50 p-4 shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 font-bold text-primary">{queueIndex + 1}</span>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold">Queue {queueIndex + 1}: {queue.label || 'Da configurare'}</p>
                            <span className={queue.label && queue.panelChannelId && queue.categoryId ? 'rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400' : 'rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400'}>
                              {queue.label && queue.panelChannelId && queue.categoryId ? 'Pronta' : 'Da completare'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">Configura il percorso completo di questa categoria.</p>
                        </div>
                      </div>
                      <Button type="button" variant="destructive" size="sm" onClick={() => removeQueue(queue.id)}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Rimuovi queue
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Nome Queue / motivazione</Label>
                        <Input
                          value={queue.label}
                          onChange={(event) => updateQueueField(queue.id, 'label', event.target.value)}
                          placeholder="es. Supporto FiveM, Whitelist, Assistenza"
                        />
                        <p className="text-xs text-muted-foreground">È il nome principale che l’utente vedrà nel menu Discord.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Emoji del menu</Label>
                        <Input
                          value={queue.emoji || ''}
                          onChange={(event) => updateQueueField(queue.id, 'emoji', event.target.value)}
                          placeholder="es. 🎮"
                        />
                      </div>
                    </div>

                    <div className="space-y-3 rounded-lg border border-violet-500/20 bg-violet-500/5 p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <Label>Sottocategorie / motivazioni</Label>
                          <p className="mt-1 text-xs text-muted-foreground">Una voce per riga. Dopo la selezione della queue, l’utente vedrà queste motivazioni nel menu Discord.</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="gap-1 self-start"
                          onClick={() => updateQueueSubcategories(queue.id, [...parseCsvIds(queue.subcategoriesCsv), 'Nuova motivazione'])}
                        >
                          <Plus className="h-4 w-4" />
                          Aggiungi voce
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {parseCsvIds(queue.subcategoriesCsv).length === 0 && (
                          <div className="rounded-md border border-dashed border-violet-500/30 p-3 text-sm text-muted-foreground">
                            Nessuna sottocategoria. Aggiungi la prima voce.
                          </div>
                        )}
                        {parseCsvIds(queue.subcategoriesCsv).map((subcategory, subcategoryIndex) => {
                          const subcategories = parseCsvIds(queue.subcategoriesCsv);
                          return (
                            <div key={`${queue.id}-subcategory-${subcategoryIndex}`} className="flex items-center gap-2">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-violet-500/15 text-sm font-semibold text-violet-600 dark:text-violet-400">{subcategoryIndex + 1}</span>
                              <Input
                                value={subcategory}
                                onChange={(event) => {
                                  const updated = [...subcategories];
                                  updated[subcategoryIndex] = event.target.value;
                                  updateQueueSubcategories(queue.id, updated);
                                }}
                                placeholder="es. Errore login"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Rimuovi sottocategoria ${subcategoryIndex + 1}`}
                                onClick={() => updateQueueSubcategories(queue.id, subcategories.filter((_, index) => index !== subcategoryIndex))}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                      <p className="mb-3 text-sm font-semibold text-blue-600 dark:text-blue-400">Destinazione ticket</p>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Canale del pannello</Label>
                        <Select
                          value={queue.panelChannelId || 'none'}
                          onValueChange={(value) => updateQueueField(queue.id, 'panelChannelId', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona canale" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Usa canale principale</SelectItem>
                            {(channels as any[]).map((channel: any) => (
                              <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Categoria apertura</Label>
                        <Select
                          value={queue.categoryId || 'none'}
                          onValueChange={(value) => updateQueueField(queue.id, 'categoryId', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Usa categoria base</SelectItem>
                            {availableCategories.map((category: any) => (
                              <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Categoria chiusura</Label>
                        <Select
                          value={queue.closedCategoryId || 'none'}
                          onValueChange={(value) => updateQueueField(queue.id, 'closedCategoryId', value === 'none' ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona categoria chiusura" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Usa categoria globale o automatica</SelectItem>
                            {availableCategories.map((category: any) => (
                              <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Il canale del pannello indica dove compare questa queue. La categoria di apertura indica dove nasce il ticket. La categoria di chiusura è opzionale: se vuota usa il valore globale.
                    </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Ruoli staff di questa queue</Label>
                      <SearchableAddSelect
                        options={(roles as any[]).map((role: any) => ({ id: role.id, label: role.name }))}
                        selectedIds={queue.staffRoleIds}
                        placeholder="Aggiungi ruolo staff"
                        searchPlaceholder="Cerca ruolo staff..."
                        emptyLabel="Nessun ruolo disponibile"
                        onAdd={(id) => toggleQueueRole(queue.id, 'staffRoleIds', id)}
                      />
                      <div className="flex flex-wrap gap-2">
                        {queue.staffRoleIds.map((roleId) => {
                          const role = (roles as any[]).find((item: any) => item.id === roleId);
                          return (
                            <Button
                              key={`${queue.id}-staff-selected-${roleId}`}
                              type="button"
                              size="sm"
                              variant="default"
                              onClick={() => toggleQueueRole(queue.id, 'staffRoleIds', roleId)}
                            >
                              {role?.name || roleId} <Trash2 className="w-3 h-3 ml-2" />
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Ruoli utente autorizzati (opzionale)</Label>
                      <SearchableAddSelect
                        options={(roles as any[]).map((role: any) => ({ id: role.id, label: role.name }))}
                        selectedIds={queue.userRoleIds}
                        placeholder="Aggiungi ruolo utente"
                        searchPlaceholder="Cerca ruolo utente..."
                        emptyLabel="Nessun ruolo disponibile"
                        onAdd={(id) => toggleQueueRole(queue.id, 'userRoleIds', id)}
                      />
                      <div className="flex flex-wrap gap-2">
                        {queue.userRoleIds.map((roleId) => {
                          const role = (roles as any[]).find((item: any) => item.id === roleId);
                          return (
                            <Button
                              key={`${queue.id}-user-selected-${roleId}`}
                              type="button"
                              size="sm"
                              variant="default"
                              onClick={() => toggleQueueRole(queue.id, 'userRoleIds', roleId)}
                            >
                              {role?.name || roleId} <Trash2 className="w-3 h-3 ml-2" />
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Istruzioni per l’AI e l’utente (opzionale)</Label>
                      <Textarea
                        rows={4}
                        value={queue.rulesText || ''}
                        onChange={(event) => updateQueueField(queue.id, 'rulesText', event.target.value)}
                        placeholder="Scrivi le regole o le informazioni utili per questa categoria..."
                      />
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                Ogni queue è un gioco: il bot usa automaticamente il canale pannello e la categoria selezionati per creare il ticket.
              </p>
              <p className="text-xs text-muted-foreground">
                I ruoli utente sono opzionali. Se non ne selezioni nessuno, il gioco sarà visibile a tutti.
              </p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="ticketReasonsCsv">Motivazioni Ticket (separate da virgola)</Label>
              <Input
                id="ticketReasonsCsv"
                value={ticketsPluginConfig.ticketReasonsCsv}
                onChange={(event) =>
                  setTicketsPluginConfig((prev) => ({ ...prev, ticketReasonsCsv: event.target.value }))
                }
                placeholder="problema generale, assistenza in game, bug, whitelist, altro"
              />
            </div>


          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => savePluginConfigMutation.mutate()} disabled={savePluginConfigMutation.isPending} variant="outline">

              {savePluginConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva instradamento e automazioni
            </Button>
            <Button
              onClick={() => spawnPanelMutation.mutate()}
              disabled={spawnPanelMutation.isPending}
            >
              {spawnPanelMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
              Pubblica pannello in Discord
            </Button>
          </div>
          {!ticketsPluginConfig.ticketPanelChannelId && (
            <p className="text-xs text-muted-foreground">Se non imposti un canale principale, verrà usato il primo canale pannello disponibile.</p>
          )}
        </CardContent>
      </Card>

      {/* Text Customization Section */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Personalizzazione Testi del Pannello & Flusso (Discord)</CardTitle>
          <CardDescription>
            Personalizza i testi dell'embed visualizzato su Discord e del flusso guidato di apertura ticket.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="panelTitle">Titolo del Pannello (Embed)</Label>
              <Input
                id="panelTitle"
                value={ticketsPluginConfig.panelTitle || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, panelTitle: e.target.value }))}
                placeholder="🎫 Supporto Ticket"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="panelButtonLabel">Testo Pulsante di Apertura</Label>
              <Input
                id="panelButtonLabel"
                value={ticketsPluginConfig.panelButtonLabel || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, panelButtonLabel: e.target.value }))}
                placeholder="Apri Ticket"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="panelDescription">Descrizione del Pannello (Embed)</Label>
              <Textarea
                id="panelDescription"
                rows={4}
                value={ticketsPluginConfig.panelDescription || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, panelDescription: e.target.value }))}
                placeholder="Premi il pulsante per iniziare il flusso guidato di supporto..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="queueSelectionTitle">Titolo Schermata Selezione Coda/Gioco</Label>
              <Input
                id="queueSelectionTitle"
                value={ticketsPluginConfig.queueSelectionTitle || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, queueSelectionTitle: e.target.value }))}
                placeholder="🎮 Seleziona la Categoria"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="queuePlaceholder">Placeholder del Menu di Selezione</Label>
              <Input
                id="queuePlaceholder"
                value={ticketsPluginConfig.queuePlaceholder || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, queuePlaceholder: e.target.value }))}
                placeholder="Seleziona la categoria..."
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="queueSelectionDescription">Descrizione Schermata Selezione Coda/Gioco</Label>
              <Textarea
                id="queueSelectionDescription"
                rows={2}
                value={ticketsPluginConfig.queueSelectionDescription || ''}
                onChange={(e) => setTicketsPluginConfig(prev => ({ ...prev, queueSelectionDescription: e.target.value }))}
                placeholder="Scegli la categoria di supporto. Hai {count} opzioni disponibili in base ai tuoi ruoli."
              />
              <p className="text-xs text-muted-foreground">Usa <code>{"{count}"}</code> per inserire dinamicamente il numero di opzioni visualizzabili per l'utente.</p>
            </div>
          </div>

          <div className="flex justify-start">
            <Button onClick={() => savePluginConfigMutation.mutate()} disabled={savePluginConfigMutation.isPending}>
              {savePluginConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva testi del pannello
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Tickets */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Ticket Recenti</CardTitle>
          <CardDescription>
            Ultimi ticket aperti nel server
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>Nessun ticket trovato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.slice(0, 10).map((ticket) => (
                <div
                  key={ticket.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      ticket.status === 'open' ? "bg-green-500" : "bg-gray-500"
                    )} />
                    <div>
                      <p className="font-medium">{ticket.subject || 'Nessun oggetto'}</p>
                      <p className="text-sm text-muted-foreground">
                        Utente: {ticket.userId} • {new Date(ticket.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium",
                      ticket.status === 'open' 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-gray-500/20 text-gray-400"
                    )}>
                      {ticket.status === 'open' ? 'Aperto' : 'Chiuso'}
                    </span>
                    {ticket.status === 'open' && (
                      <>
                        <Input
                          className="w-40 h-8"
                          placeholder="Assignee ID"
                          value={assignInputs[ticket.id] || ''}
                          onChange={(e) => setAssignInputs((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => assignTicketMutation.mutate({ ticketId: ticket.id, userId: assignInputs[ticket.id] || undefined })}
                          disabled={assignTicketMutation.isPending}
                        >
                          Assegna
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => closeTicketMutation.mutate(ticket.id)}
                          disabled={closeTicketMutation.isPending}
                        >
                          Chiudi
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button size="lg" onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salva impostazioni di base
        </Button>
      </div>
    </div>
  );
};
